"use client";

import { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Minus, Plus } from "lucide-react";
import { useLocale } from "next-intl";
import { useGetServices } from "@/app/hooks/main-hooks";

export default function ServicesSection() {
  const [active, setActive] = useState<number | null>(null);
  const locale = useLocale();
  const { data: servicesData } = useGetServices();

  const currentServices = servicesData?.services || [];

  return (
    <section
      id="services"
      className="relative py-24 overflow-hidden bg-[#f7f9fc]"
    >
      {/* Background Blobs */}
      <motion.div
        animate={{ y: [0, -40, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-20 left-10 w-96 h-96 bg-[#c9a24d]/5 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ y: [0, 40, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-20 right-10 w-80 h-80 bg-[#e6c77a]/5 rounded-full blur-3xl"
      />

      <div className="container mx-auto px-6 relative z-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0a1b2e] mb-6">
            {locale === "en" && "Our"}{" "}
            <span className="bg-linear-to-r from-[#c9a24d] via-[#e6c77a] to-brand-mustard bg-clip-text text-transparent">
              {locale === "en" ? "Services" : "الخدمات"}
            </span>
          </h2>

          <p className="text-lg md:text-xl text-[#0a1b2e]/70 max-w-3xl mx-auto">
            {locale === "en"
              ? "Enterprise-grade ERP services designed for clarity, control, and scalable growth."
              : "خدمات ERP على مستوى المؤسسات مصممة للوضوح والسيطرة والنمو القابل للتوسع."}
          </p>
        </motion.div>

        {/* GRID LAYOUT */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
          {currentServices.map((service: any, index: number) => {
            const isActive = active === index;

            return (
              <motion.div
                key={index}
                layout
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group relative rounded-3xl bg-white border border-[#c9a24d]/20 overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:shadow-[#c9a24d]/20 hover:border-[#c9a24d]/50 h-fit"
              >
                {/* Image */}
                <div className="relative w-full h-56 overflow-hidden">
                  <Image
                    src={service.thumbnail}
                    alt={service?.name?.[locale]}
                    fill
                    className="object-cover transition-transform duration-1000 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-linear-to-tr from-[#0a1b2e]/40 via-transparent to-transparent" />
                </div>

                {/* Header */}
                <button
                  className="w-full text-left p-6 space-y-4"
                  onMouseMove={() => setActive(isActive ? null : index)}
                  onMouseLeave={() => setActive(null)}
                >
                  <h3 className="text-2xl font-bold text-[#0a1b2e]">
                    {service?.name?.[locale]}
                  </h3>

                  <p className="text-[#c9a24d] font-medium">
                    {service?.subDescription?.[locale]}
                  </p>

                  <div className="flex items-center justify-between text-[#c9a24d]">
                    <motion.div
                      animate={{ rotate: isActive ? 180 : 0 }}
                      transition={{ duration: 0.4 }}
                      className="w-10 h-10 rounded-full bg-[#c9a24d]/10 flex items-center justify-center border border-[#c9a24d]/30"
                    >
                      {isActive ? <Minus size={22} /> : <Plus size={22} />}
                    </motion.div>

                    <ArrowUpRight
                      size={26}
                      className="transition-all duration-500 group-hover:translate-x-2 group-hover:-translate-y-2"
                    />
                  </div>
                </button>

                {/* Expandable Description */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-[#0a1b2e]/80 leading-relaxed">
                        {service?.detailedDescription?.[locale]}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Gold Bar */}
                <motion.div
                  className="h-1 w-full bg-linear-to-r from-[#c9a24d] via-[#e6c77a] to-[#c9a24d]"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: isActive ? 1 : 0 }}
                  transition={{ duration: 0.6 }}
                  style={{ originX: 0 }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
